'use client';

import { Tabs, TabsList, TabsTrigger } from '@tallinn-arvudes/ui/atoms/tabs';
import { cn } from '@tallinn-arvudes/ui/utils';
import * as React from 'react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

export const MobileTabs = ({
  children,
  metricsCount,
  pagesCount,
  statisticsCount,
}: {
  children: ReactNode;
  pagesCount: number;
  metricsCount: number;
  statisticsCount: number;
}) => {
  const tabsListRef = useRef<HTMLUListElement>(null);

  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  useEffect(() => {
    const tabsList = tabsListRef.current;

    if (!tabsList) {
      return;
    }

    const onScroll = () => {
      const scrollLeft = tabsList.scrollLeft;
      const scrollWidth = tabsList.scrollWidth;
      const clientWidth = tabsList.clientWidth;

      if (scrollWidth - clientWidth < 5) {
        setShowLeft(false);
        setShowRight(false);
        return;
      }
      setShowLeft(!!scrollLeft);
      setShowRight(scrollWidth - clientWidth - scrollLeft > 5);
    };
    onScroll();

    window.addEventListener('resize', onScroll);
    tabsList.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('resize', onScroll);
      tabsList.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <div className="flex justify-center lg:hidden">
      <Tabs defaultValue="lehed" className="tds-tabs w-[80%]">
        <div className="relative mx-auto">
          <button
            className={cn(
              'tds-tabs__scroll-button tds-tabs__scroll-left absolute -translate-x-full top-1/2 -translate-y-1/2',
              {
                block: showLeft,
              },
            )}
            aria-label="Keri vasakule"
            onClick={() => {
              if (tabsListRef.current) {
                tabsListRef.current?.scrollTo({
                  top: 0,
                  left: tabsListRef.current.scrollLeft - 200,
                  behavior: 'smooth',
                });
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.3184 4.76904L16.6831 6.23115L9.96634 12.5001L16.6831 18.769L15.3184 20.2312L7.03516 12.5001L15.3184 4.76904Z"
                fill="#2A2C2D"
              />
            </svg>
          </button>
          <div className="tds-tabs__scroll-container">
            <TabsList defaultValue="lehed" tablistRef={tabsListRef}>
              <TabsTrigger
                onFocus={() => {
                  if (tabsListRef.current) {
                    tabsListRef.current?.scrollTo({
                      top: 0,
                      left: tabsListRef.current.scrollLeft - 200,
                      behavior: 'instant',
                    });
                  }
                }}
                value="lehed"
              >
                Lehed ({pagesCount})
              </TabsTrigger>
              <TabsTrigger value="moodikud">
                Mõõdikud ({metricsCount})
              </TabsTrigger>
              <TabsTrigger
                onFocus={() => {
                  if (tabsListRef.current) {
                    tabsListRef.current?.scrollTo({
                      top: 0,
                      left: tabsListRef.current.scrollLeft + 200,
                      behavior: 'instant',
                    });
                  }
                }}
                value="statistika"
              >
                Statistika ({statisticsCount})
              </TabsTrigger>
            </TabsList>
          </div>
          <button
            className={cn(
              'tds-tabs__scroll-button tds-tabs__scroll-right absolute translate-x-full top-1/2 -translate-y-1/2',
              {
                block: showRight,
              },
            )}
            aria-label="Keri paremale"
            onClick={() => {
              if (tabsListRef.current) {
                tabsListRef.current?.scrollTo({
                  top: 0,
                  left: tabsListRef.current.scrollLeft + 200,
                  behavior: 'smooth',
                });
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M14.0351 12.5001L7.31836 6.23115L8.683 4.76904L16.9663 12.5001L8.683 20.2312L7.31836 18.769L14.0351 12.5001Z"
                fill="#2A2C2D"
              />
            </svg>
          </button>
        </div>
        {children}
      </Tabs>
    </div>
  );
};
